import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Receipt, Search, ArrowUpRight, ArrowDownRight,
  IndianRupee, Filter
} from 'lucide-react';
import AdminLayout from '../../components/layouts/AdminLayout';
import { apiClient } from '../../App';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const TransactionsPage = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      const response = await apiClient.get('/transactions');
      setTransactions(response.data);
    } catch (error) {
      toast.error('Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'contribution':
        return <ArrowDownRight className="w-5 h-5 text-green-600" />;
      case 'loan_disbursement':
        return <ArrowUpRight className="w-5 h-5 text-blue-600" />;
      case 'emi_payment':
        return <IndianRupee className="w-5 h-5 text-purple-600" />;
      case 'penalty':
        return <IndianRupee className="w-5 h-5 text-red-600" />;
      case 'interest':
        return <IndianRupee className="w-5 h-5 text-orange-600" />;
      default:
        return <IndianRupee className="w-5 h-5 text-gray-600" />;
    }
  };

  const getTypeStyle = (type) => {
    switch (type) {
      case 'contribution':
        return 'bg-green-100';
      case 'loan_disbursement':
        return 'bg-blue-100';
      case 'emi_payment':
        return 'bg-purple-100';
      case 'penalty':
        return 'bg-red-100';
      case 'interest':
        return 'bg-orange-100';
      default:
        return 'bg-gray-100';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'contribution':
      case 'emi_payment':
      case 'interest':
        return 'text-green-600';
      case 'loan_disbursement':
        return 'text-blue-600';
      case 'penalty':
        return 'text-red-600';
      default:
        return 'text-[#0A2540]';
    }
  };

  const filteredTransactions = transactions.filter(tx => {
    const matchesSearch = tx.member_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         tx.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || tx.type === typeFilter;
    return matchesSearch && matchesType;
  });

  const stats = {
    totalContributions: transactions.filter(t => t.type === 'contribution').reduce((s, t) => s + t.amount, 0),
    totalDisbursements: transactions.filter(t => t.type === 'loan_disbursement').reduce((s, t) => s + t.amount, 0),
    totalEmiPayments: transactions.filter(t => t.type === 'emi_payment').reduce((s, t) => s + t.amount, 0),
    totalInterest: transactions.filter(t => t.type === 'interest').reduce((s, t) => s + t.amount, 0),
    totalPenalties: transactions.filter(t => t.type === 'penalty').reduce((s, t) => s + t.amount, 0),
  };

  return (
    <AdminLayout title="Transactions" subtitle="Complete transaction ledger">
      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between mb-6">
        <div className="flex gap-4 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#425466]" />
            <Input
              placeholder="Search transactions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-transactions-input"
            />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-44" data-testid="tx-type-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="contribution">Contributions</SelectItem>
              <SelectItem value="loan_disbursement">Loan Disbursements</SelectItem>
              <SelectItem value="emi_payment">EMI Payments</SelectItem>
              <SelectItem value="interest">Interest</SelectItem>
              <SelectItem value="penalty">Penalties</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="dashboard-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
              <ArrowDownRight className="w-4 h-4 text-green-600" />
            </div>
            <span className="text-xs text-[#425466]">Contributions</span>
          </div>
          <p className="text-lg font-bold text-green-600">{formatCurrency(stats.totalContributions)}</p>
        </div>
        <div className="dashboard-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <ArrowUpRight className="w-4 h-4 text-blue-600" />
            </div>
            <span className="text-xs text-[#425466]">Disbursed</span>
          </div>
          <p className="text-lg font-bold text-blue-600">{formatCurrency(stats.totalDisbursements)}</p>
        </div>
        <div className="dashboard-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
              <IndianRupee className="w-4 h-4 text-purple-600" />
            </div>
            <span className="text-xs text-[#425466]">EMI Received</span>
          </div>
          <p className="text-lg font-bold text-purple-600">{formatCurrency(stats.totalEmiPayments)}</p>
        </div>
        <div className="dashboard-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
              <IndianRupee className="w-4 h-4 text-orange-600" />
            </div>
            <span className="text-xs text-[#425466]">Interest</span>
          </div>
          <p className="text-lg font-bold text-orange-600">{formatCurrency(stats.totalInterest)}</p>
        </div>
        <div className="dashboard-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
              <IndianRupee className="w-4 h-4 text-red-600" />
            </div>
            <span className="text-xs text-[#425466]">Penalties</span>
          </div>
          <p className="text-lg font-bold text-red-600">{formatCurrency(stats.totalPenalties)}</p>
        </div>
      </div>

      {/* Transactions Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="dashboard-card overflow-hidden"
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-[#F4F7FB] border-b border-gray-100">
                <th className="text-left px-6 py-4 text-xs font-semibold text-[#425466] uppercase tracking-wider">Date</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-[#425466] uppercase tracking-wider">Member</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-[#425466] uppercase tracking-wider">Type</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-[#425466] uppercase tracking-wider">Description</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-[#425466] uppercase tracking-wider">Amount</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-[#425466] uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                [...Array(10)].map((_, i) => (
                  <tr key={i}>
                    <td className="px-6 py-4"><div className="skeleton h-6 w-24 rounded"></div></td>
                    <td className="px-6 py-4"><div className="skeleton h-6 w-32 rounded"></div></td>
                    <td className="px-6 py-4"><div className="skeleton h-6 w-24 rounded-full"></div></td>
                    <td className="px-6 py-4"><div className="skeleton h-6 w-48 rounded"></div></td>
                    <td className="px-6 py-4"><div className="skeleton h-6 w-20 rounded"></div></td>
                    <td className="px-6 py-4"><div className="skeleton h-6 w-16 rounded-full"></div></td>
                  </tr>
                ))
              ) : filteredTransactions.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-[#425466]">
                    <Receipt className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>No transactions found</p>
                  </td>
                </tr>
              ) : (
                filteredTransactions.map((tx) => (
                  <tr key={tx.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-medium text-[#0A2540]">
                          {new Date(tx.created_at).toLocaleDateString('en-IN')}
                        </p>
                        <p className="text-xs text-[#425466]">
                          {new Date(tx.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#0A2540] flex items-center justify-center text-white text-sm font-medium">
                          {tx.member_name.charAt(0)}
                        </div>
                        <span className="font-medium text-[#0A2540]">{tx.member_name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-lg ${getTypeStyle(tx.type)} flex items-center justify-center`}>
                          {getTypeIcon(tx.type)}
                        </div>
                        <span className="text-sm capitalize text-[#425466]">{tx.type.replace('_', ' ')}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-[#425466] max-w-xs truncate">{tx.description}</p>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`font-semibold ${getTypeColor(tx.type)}`}>
                        {['contribution', 'emi_payment', 'interest', 'penalty'].includes(tx.type) ? '+' : '-'}
                        {formatCurrency(tx.amount)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-600">
                        {tx.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Summary */}
      <div className="mt-6 text-center text-sm text-[#425466]">
        Showing {filteredTransactions.length} of {transactions.length} transactions
      </div>
    </AdminLayout>
  );
};

export default TransactionsPage;
